<!--
  Settings → Notifications, tab "Mail check": the mail-tester.com score of a
  test quotation mail and a test invoice mail every N days, with an alert under
  a threshold (routes /api/mail-check, migration 084).

  Lived in Dependency Sync until 2026-10-18. Emits `state` (tone of the last
  check) for the tab bar.
-->
<template>
  <vx-card>
    <hv-section
      title="Mail check"
      sub="Every few days, the software sends a test quotation mail and a test invoice mail, built like the real ones, to mail-tester.com and reads the score out of 10. Under the threshold, or when a test fails, an alert mail leaves (line “Mail check alert” in the Automatic mails tab).">
      <template slot="actions">
        <vs-button size="small" type="border" icon-pack="feather" icon="icon-refresh-cw"
                   :disabled="mcLoading" @click="loadMailCheck">Refresh</vs-button>
        <vs-button size="small" class="ml-2" icon-pack="feather" icon="icon-play"
                   :disabled="mc.running || mcStarting" @click="confirmRunMailCheck">
          {{ mc.running ? 'Running…' : 'Run now' }}
        </vs-button>
      </template>

      <div v-if="mcError" class="hv2-error"><b>The mail check could not be read.</b><br />{{ mcError }}</div>

      <div v-else class="vx-row">
        <!-- The state -->
        <div class="vx-col w-full lg:w-1/2 mb-base">
          <div class="hv2-sync-card">
            <div class="hv2-sync-head">
              <span class="hv2-sync-title">Last check</span>
              <hv-pill class="ml-auto" :tone="mcState.tone" :label="mcState.label" :icon="mcState.icon" />
            </div>
            <div class="hv2-kv"><div class="hv2-kv-k">Scores</div>
              <div class="hv2-kv-v" :class="{ 'is-empty': !mc.last_batch.length }">
                <template v-if="mc.last_batch.length">
                  <div v-for="r in mc.last_batch" :key="r.id" class="sumal-mc-score">
                    <b>{{ sampleName(r.sample) }}</b>: {{ scoreText(r) }}
                    <hv-pill :tone="mcTone(r.status)" :label="mcLabel(r.status)" class="ml-1" />
                    <span v-if="r.issues_summary" class="hv2-muted"> · {{ r.issues_summary }}</span>
                    <span v-if="r.error" class="hv2-sync-bad"> · {{ r.error }}</span>
                  </div>
                </template>
                <template v-else>never</template>
              </div></div>
            <div class="hv2-kv"><div class="hv2-kv-k">When</div>
              <div class="hv2-kv-v">
                <template v-if="mc.last_batch.length">
                  {{ roWhen(mc.last_batch[0].started_at) }} · started by {{ mc.last_batch[0].triggered_by || DASH }}
                  <span v-if="mc.last_batch[0].alerted_at"> · alert sent</span>
                </template>
                <template v-else>{{ DASH }}</template>
              </div></div>
            <div class="hv2-kv"><div class="hv2-kv-k">Next check</div>
              <div class="hv2-kv-v">
                <template v-if="mc.next_run_at">
                  {{ roWhen(mc.next_run_at) }}
                  <span class="hv2-muted">· every {{ mc.settings.every_days }} day(s) at {{ mc.settings.run_at }}, Romania time</span>
                </template>
                <span v-else class="hv2-muted">Off. "Run now" still works.</span>
              </div></div>
            <div class="hv2-kv"><div class="hv2-kv-k">Mailbox</div>
              <div class="hv2-kv-v" v-if="mc.mailbox">
                <span class="hv2-code">{{ mc.mailbox.prefix }}-xylo-…@{{ mc.mailbox.domain }}</span>
                <span class="hv2-muted">· {{ mc.mailbox.free ? 'free version: 3 tests per 24 h, results kept 7 days' : 'account prefix: results kept 30 days' }}</span>
              </div></div>
            <div class="hv2-kv"><div class="hv2-kv-k">Threshold</div>
              <div class="hv2-kv-v">{{ mc.settings.threshold }}/10 <span class="hv2-muted">· under it, an alert mail leaves</span></div></div>
            <div class="hv2-kv"><div class="hv2-kv-k">Test mails</div>
              <div class="hv2-kv-v">
                <a href="#" @click.prevent="previewSample('quotation')">See the quotation mail</a> ·
                <a href="#" @click.prevent="previewSample('invoice')">See the invoice mail</a>
                <span class="hv2-muted">· built like the real ones, with a PDF, invented figures</span>
              </div></div>
          </div>
        </div>

        <!-- The settings -->
        <div class="vx-col w-full lg:w-1/2 mb-base">
          <div class="hv2-sync-card">
            <div class="hv2-sync-head"><span class="hv2-sync-title">Settings</span></div>
            <div class="sumal-settings">
              <div class="sumal-settings-row">
                <vs-switch v-model="mcForm.enabled" />
                <span class="ml-2">{{ mcForm.enabled ? 'On: runs by itself' : 'Off: only "Run now"' }}</span>
              </div>
              <div class="vx-row items-end">
                <div class="vx-col md:w-1/3 w-full mb-3">
                  <vs-input type="number" min="1" max="90" class="w-full" label="Every … days" v-model.number="mcForm.every_days" />
                </div>
                <div class="vx-col md:w-1/3 w-full mb-3">
                  <vs-input class="w-full" label="At (Romania time)" placeholder="07:30" v-model="mcForm.run_at" />
                </div>
                <div class="vx-col md:w-1/3 w-full mb-3">
                  <vs-input type="number" min="0" max="10" step="0.5" class="w-full" label="Alert under … /10" v-model.number="mcForm.threshold" />
                </div>
                <div class="vx-col md:w-1/2 w-full mb-3">
                  <vs-input class="w-full" label="mail-tester.com account prefix (optional)" placeholder="empty = free version" v-model="mcForm.mt_username" />
                </div>
                <div class="vx-col md:w-1/2 w-full mb-3">
                  <vs-select v-model="mcForm.language" label="Language of the test mails" class="w-full">
                    <vs-select-item value="fr" text="French" />
                    <vs-select-item value="en" text="English" />
                  </vs-select>
                </div>
              </div>
              <p class="hv2-sec-sub mb-3">Without a prefix, the free version of mail-tester.com is used: 3 tests per 24 hours, so one check (2 tests) a day at most, and "Run now" counts too. With a paid prefix, the limit is the account's. Recipients of the alert: line "Mail check alert" in the Automatic mails tab.</p>
              <div class="sumal-settings-actions">
                <vs-button size="small" :disabled="mcSaving" @click="saveMailCheck">{{ mcSaving ? 'Saving…' : 'Save' }}</vs-button>
                <span v-if="mc.settings.updated_at" class="hv2-muted ml-3">
                  saved {{ roWhen(mc.settings.updated_at) }}<template v-if="mc.settings.updated_by"> by {{ mc.settings.updated_by }}</template>
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- The history of the scores -->
      <div v-if="!mcError" class="hv2-subhead">History</div>
      <hv-table v-if="!mcError" :rows="mc.runs" :loading="mcLoading && !mc.runs.length" row-key="id" pagination :max-items="10"
                empty-icon="MailIcon" empty-text="No check yet. Click “Run now”.">
        <template slot="thead">
          <vs-th>When</vs-th><vs-th>Mail</vs-th><vs-th>Score</vs-th><vs-th>What mail-tester says</vs-th><vs-th>Started by</vs-th><vs-th></vs-th>
        </template>
        <template slot="row" slot-scope="{ r }">
          <vs-td>{{ roWhen(r.started_at) }}</vs-td>
          <vs-td>{{ sampleName(r.sample) }}<div v-if="r.subject" class="hv2-muted">{{ r.subject }}</div></vs-td>
          <vs-td>
            <hv-pill :tone="mcTone(r.status)" :label="`${scoreText(r)} · ${mcLabel(r.status)}`" />
            <div class="hv2-muted">threshold {{ r.threshold }}</div>
          </vs-td>
          <vs-td>
            <span v-if="r.error" class="hv2-sync-bad">{{ r.error }}</span>
            <span v-else-if="r.issues_summary">{{ r.issues_summary }}</span>
            <span v-else-if="r.status === 'ok'" class="hv2-muted">nothing to report</span>
            <span v-else class="hv2-muted">{{ DASH }}</span>
            <div v-if="r.alerted_at" class="hv2-muted">alert sent {{ roWhen(r.alerted_at) }}</div>
          </vs-td>
          <vs-td>{{ r.triggered_by || DASH }}</vs-td>
          <vs-td class="sumal-check-actions">
            <vs-button size="small" type="flat" icon-pack="feather" icon="icon-eye" title="All the details"
                       :disabled="r.status === 'running'" @click="openRunDetail(r)" />
            <a v-if="r.report_url" :href="r.report_url" target="_blank" rel="noopener" title="Open the report on mail-tester.com">
              <vs-button size="small" type="flat" icon-pack="feather" icon="icon-external-link" />
            </a>
          </vs-td>
        </template>
      </hv-table>
    </hv-section>

    <mail-preview :open.sync="previewOpen" :loading="previewLoading" :subject="previewSubject" :html="previewHtml" />

    <!-- One mail-tester result in full. Always mounted (Vuesax moves the popup into document.body). -->
    <vs-popup title="Mail check result" :active.sync="mcDetailOpen">
      <div v-if="mcDetailLoading" class="hv2-loading">
        <vs-progress indeterminate color="primary" class="mb-2" />Reading…
      </div>
      <div v-else-if="mcDetail" class="hv2">
        <p class="hv2-sec-sub">
          <b>{{ mcDetail.name }}</b> · {{ roWhen(mcDetail.started_at) }} · score <b>{{ scoreText(mcDetail) }}</b> (threshold {{ mcDetail.threshold }})<br />
          <span v-if="mcDetail.subject">Subject: {{ mcDetail.subject }}<br /></span>
          Sent to {{ mcDetail.address }}<template v-if="mcDetail.report_url"> · <a :href="mcDetail.report_url" target="_blank" rel="noopener">open the report on mail-tester.com</a></template>
        </p>
        <div v-if="mcDetail.error" class="hv2-error">{{ mcDetail.error }}</div>
        <template v-if="detailSections(mcDetail).length">
          <div class="hv2-subhead">Sections</div>
          <div v-for="s in detailSections(mcDetail)" :key="s.key" class="hv2-kv">
            <div class="hv2-kv-k">{{ s.title }}</div>
            <div class="hv2-kv-v">{{ s.mark }} <span class="hv2-muted">{{ s.status }}</span></div>
          </div>
        </template>
        <template v-if="(mcDetail.issues || []).length">
          <div class="hv2-subhead mt-3">What to fix</div>
          <ul class="sumal-mc-issues">
            <li v-for="(i, idx) in mcDetail.issues" :key="idx">
              <b>{{ i.code }}</b><span v-if="i.score !== null && i.score !== undefined"> ({{ i.score }})</span><span v-if="i.text"> — {{ i.text }}</span>
              <div v-if="i.solution" class="hv2-muted">{{ i.solution }}</div>
            </li>
          </ul>
        </template>
        <template v-if="detailRules(mcDetail).length">
          <div class="hv2-subhead mt-3">All SpamAssassin rules</div>
          <ul class="sumal-mc-issues">
            <li v-for="(r, idx) in detailRules(mcDetail)" :key="idx"><b>{{ r.code }}</b> {{ r.score }} <span class="hv2-muted">{{ r.text }}</span></li>
          </ul>
        </template>
      </div>
    </vs-popup>
  </vx-card>
</template>

<script>
import axios from '@/axios.js'
import HvSection from '@/views/harvesting2/components/HvSection'
import HvTable from '@/views/harvesting2/components/HvTable'
import HvPill from '@/views/harvesting2/components/HvPill'
import formatMixin from '@/views/harvesting2/formatMixin'
import MailPreview from './MailPreview'
import { roWhen } from '@/helpers/romaniaTime'

export default {
  name: 'MailCheck',
  components: { HvSection, HvTable, HvPill, MailPreview },
  mixins: [formatMixin],
  data () {
    return {
      mc: { settings: {}, samples: [], running: false, next_run_at: null, last_cron_at: null, last_batch: [], runs: [] },
      mcLoading: false,
      mcError: '',
      mcForm: { enabled: false, every_days: 7, run_at: '07:30', threshold: 9, language: 'fr', mt_username: '' },
      mcSaving: false,
      mcStarting: false,
      mcPoll: null,
      mcDetailOpen: false,
      mcDetail: null,
      mcDetailLoading: false,
      previewOpen: false,
      previewLoading: false,
      previewHtml: '',
      previewSubject: ''
    }
  },
  computed: {
    mcState () {
      const b = this.mc.last_batch || []
      if (this.mc.running) return { tone: 'info', label: 'Running', icon: 'ClockIcon' }
      if (!b.length) return { tone: '', label: 'Never ran', icon: 'HelpCircleIcon' }
      if (b.some((r) => r.status === 'failed')) return { tone: 'danger', label: 'Last check failed', icon: 'XCircleIcon' }
      if (b.some((r) => r.status === 'low')) return { tone: 'danger', label: 'Under the threshold', icon: 'AlertTriangleIcon' }
      return { tone: 'ok', label: 'All good', icon: 'CheckCircleIcon' }
    }
  },
  watch: {
    mcState: { immediate: true, handler (v) { this.$emit('state', v) } }
  },
  methods: {
    roWhen,
    notify (title, text, color) {
      this.$vs.notify({ title, text, color, iconPack: 'feather', icon: color === 'danger' ? 'icon-alert-circle' : 'icon-check', position: 'bottom-right', time: 6000 })
    },
    errText (e) { return (e.response && e.response.data && e.response.data.message) || e.message },
    sampleName (k) { const s = (this.mc.samples || []).find((x) => x.key === k); return s ? s.name : k },
    scoreText (r) { return r.score === null || r.score === undefined ? '—' : `${Number(r.score)}/${Number(r.max_score) || 10}` },
    mcTone (st) { return st === 'ok' ? 'ok' : st === 'running' ? 'info' : 'danger' },
    mcLabel (st) { return st === 'ok' ? 'OK' : st === 'low' ? 'Under threshold' : st === 'failed' ? 'Failed' : 'Waiting' },
    loadMailCheck () {
      this.mcLoading = true
      this.mcError = ''
      axios.get('/mail-check')
        .then((r) => {
          this.mc = r.data
          const s = r.data.settings || {}
          this.mcForm = {
            enabled: !!s.enabled,
            every_days: s.every_days || 7,
            run_at: s.run_at || '07:30',
            threshold: s.threshold === undefined || s.threshold === null ? 9 : Number(s.threshold),
            language: s.language || 'fr',
            mt_username: s.mt_username || ''
          }
          this.scheduleMcPoll()
        })
        .catch((e) => { this.mcError = this.errText(e) })
        .finally(() => { this.mcLoading = false })
    },
    // While a check runs, the page re-reads the status every 15 s.
    scheduleMcPoll () {
      if (this.mcPoll) { clearTimeout(this.mcPoll); this.mcPoll = null }
      if (this.mc.running) this.mcPoll = setTimeout(() => this.loadMailCheck(), 15000)
    },
    saveMailCheck () {
      this.mcSaving = true
      axios.put('/mail-check/settings', this.mcForm)
        .then((r) => {
          this.notify('Saved', r.data.next_run_at ? `Next check ${this.roWhen(r.data.next_run_at)}` : 'Mail check is off', 'success')
          this.loadMailCheck()
          this.$emit('saved')
        })
        .catch((e) => this.notify('Not saved', this.errText(e), 'danger'))
        .finally(() => { this.mcSaving = false })
    },
    confirmRunMailCheck () {
      this.$vs.dialog({
        type: 'confirm',
        color: 'primary',
        title: 'Run the mail check now?',
        text: 'A test quotation mail and a test invoice mail go to mail-tester.com (2 of the 3 free tests of the day). The scores come back in a few minutes.',
        acceptText: 'Run',
        cancelText: 'Cancel',
        accept: () => this.runMailCheck()
      })
    },
    runMailCheck () {
      this.mcStarting = true
      axios.post('/mail-check/run')
        .then(() => { this.notify('Check started', 'The scores come back in a few minutes.', 'success'); this.loadMailCheck() })
        .catch((e) => this.notify('Not started', this.errText(e), 'danger'))
        .finally(() => { this.mcStarting = false })
    },
    previewSample (k) {
      this.previewOpen = true
      this.previewLoading = true
      this.previewHtml = ''
      this.previewSubject = ''
      axios.get(`/mail-check/preview/${k}`)
        .then((r) => {
          this.previewHtml = r.data.html
          this.previewSubject = `${r.data.subject} — to ${r.data.to}, reply-to ${r.data.reply_to}, PDF ${r.data.attachment.filename} (${Math.round(r.data.attachment.size / 1024)} KB)`
        })
        .catch((e) => { this.notify('Cannot show the mail', this.errText(e), 'danger'); this.previewOpen = false })
        .finally(() => { this.previewLoading = false })
    },
    openRunDetail (r) {
      this.mcDetailOpen = true
      this.mcDetailLoading = true
      this.mcDetail = null
      axios.get(`/mail-check/runs/${r.id}`)
        .then((res) => { this.mcDetail = res.data })
        .catch((e) => { this.notify('Cannot show the result', this.errText(e), 'danger'); this.mcDetailOpen = false })
        .finally(() => { this.mcDetailLoading = false })
    },
    // The five sections of mail-tester, with their mark, from the kept JSON.
    detailSections (d) {
      const j = (d && d.details) || {}
      const out = []
      for (const k of ['spamAssassin', 'signature', 'body', 'blacklists', 'links']) {
        const o = j[k]
        if (o && typeof o === 'object') out.push({ key: k, title: o.title || k, mark: o.displayedMark || (o.mark !== undefined ? o.mark : ''), status: o.statusClass || o.status || '' })
      }
      return out
    },
    detailRules (d) {
      const j = (d && d.details) || {}
      const rules = j.spamAssassin && Array.isArray(j.spamAssassin.rules) ? j.spamAssassin.rules : []
      return rules.map((r) => ({ code: r.code, score: r.score, text: r.description || '', status: r.statusClass || r.status || '' }))
    }
  },
  created () { this.loadMailCheck() },
  beforeDestroy () {
    if (this.mcPoll) clearTimeout(this.mcPoll)
  }
}
</script>
